import * as ActionTypes from '../actions/actionTypes';
import {DATA_SETS} from '../Constants';
import {
  calculateTableData,
  convertToGraphData,
  tableMetaData
} from '../utils/behaviour_models/shared/behaviour_models_utils';
import {runParityCalcsFromConfig} from '../utils/behaviour_models/shared/parity';
import {emptyGraphMap, extractTicks} from '../utils/point_utils';
import {
  calculateDIR,
  calculateFFM_C,
  calculateFFM_DT,
  calculateFFM_NT,
  calculateFFM_PM,
  calculateFH,
  calculateFI,
  calculateFROS,
  calculateFROS_NS,
  calculateROS_MF,
  calculateROS_SF,
  calculateSD,
  calculateTFL
} from '../utils/behaviour_models/vesta/calculations';
import {
  DEFAULT_BARK_HAZARD,
  DEFAULT_ELEVATED_HAZARD,
  DEFAULT_ELEVATED_HEIGHT,
  DEFAULT_NEAR_SURFACE_HAZARD,
  DEFAULT_NEAR_SURFACE_HEIGHT,
  DEFAULT_SLOPE,
  DEFAULT_SUMMER_AFTERNOON_FMC,
  DEFAULT_SURFACE_HAZARD
} from '../utils/behaviour_models/vesta/constants';

const extractVariables = (state) => {
  return {
    slope: state.slope,
    surface_hazard: state.surface_hazard,
    near_surface_hazard: state.near_surface_hazard,
    near_surface_height: state.near_surface_height,
    elevated_hazard: state.elevated_hazard,
    elevated_height: state.elevated_height,
    bark_hazard: state.bark_hazard,
    lag_temp_rh: state.lag_temp_rh,
    summer_afternoon_fmc: state.summer_afternoon_fmc
  };
};

const VALUES_CONFIG = [
  { key: 'DIR', displayName: 'Direction of Spread (°TN)', calc: calculateDIR, decPlaces: 0, arrow: 'fa-arrow-up' },
  { key: 'FFM_PM', displayName: 'FMC Summer PM (%)', calc: calculateFFM_PM, decPlaces: 1, nonFinal: true },
  { key: 'FFM_DT', displayName: 'FMC Daytime (%)', calc: calculateFFM_DT, decPlaces: 1, nonFinal: true },
  { key: 'FFM_NT', displayName: 'FMC Nighttime (%)', calc: calculateFFM_NT, decPlaces: 1, nonFinal: true },
  { key: 'FFM_C', displayName: 'Current FMC (%)', calc: calculateFFM_C, decPlaces: 1 },
  { key: 'ROS_MF', displayName: 'ROS Moisture Factor (Mf)', calc: calculateROS_MF, decPlaces: 2, nonFinal: true },
  { key: 'ROS_SF', displayName: 'ROS Slope Factor (Sf)', calc: calculateROS_SF, decPlaces: 1, nonFinal: true },
  {
    key: 'FROS', displayName: 'FROS + slope (m/h)', calc: calculateFROS, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 1000.0,
      leftColor: 'rgba(238, 46, 36, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  { key: 'FROS_NS', displayName: 'Flank ROS (no slope no wind) (m/h)', calc: calculateFROS_NS, decPlaces: 0 },
  {
    key: 'FH', displayName: 'Flame Height (m)', calc: calculateFH, decPlaces: 1,
    colors: {
      scale: {
        '1.5': 'rgba(122, 193, 66, 0.7)',
        '4': 'rgba(248, 152, 41, 0.7)'
      },
      else: 'rgba(238, 46, 36, 0.7)'
    }
  },
  {
    key: 'SD', displayName: 'Spotting Distance (m)', calc: calculateSD, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 2000.0,
      leftColor: 'rgba(238, 46, 36, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  { key: 'TFL', displayName: 'Total Fuel Load (t/ha)', calc: calculateTFL, decPlaces: 0, nonFinal: true },
  {
    key: 'FI', displayName: 'Fireline Intensity (kW/m)', calc: calculateFI, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 15000.0,
      leftColor: 'rgba(248, 152, 41, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  }
];

const GRAPHS_CONFIG = [
  {
    title: 'Forward Rate of Spread',
    id: 'FROS',
    type: 'LineChart',
    YAxisLabel: 'm/h',
    series: [
      {
        name: 'With Slope',
        dataKey: 'FROS',
        type: 'monotone',
        stroke: '#FF5131',
        active: true
      },
      {
        name: 'No Slope, No Wind',
        dataKey: 'FROS_NS',
        type: 'monotone',
        stroke: '#008CC4',
        active: true
      }
    ]
  }
];

const initialState = {
  lag_temp_rh: 0,
  summer_afternoon_fmc: DEFAULT_SUMMER_AFTERNOON_FMC,
  slope: DEFAULT_SLOPE,
  surface_hazard: DEFAULT_SURFACE_HAZARD,
  near_surface_hazard: DEFAULT_NEAR_SURFACE_HAZARD,
  near_surface_height: DEFAULT_NEAR_SURFACE_HEIGHT,
  elevated_hazard: DEFAULT_ELEVATED_HAZARD,
  elevated_height: DEFAULT_ELEVATED_HEIGHT,
  bark_hazard: DEFAULT_BARK_HAZARD,
  calculated_fuel_load: 0,
  table_data: {},
  graph_data: emptyGraphMap(GRAPHS_CONFIG),
  table_meta_data: tableMetaData(VALUES_CONFIG, DATA_SETS.VESTA),
  graph_meta_data: GRAPHS_CONFIG
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.SET_BEHAVIOUR_VARIABLES:
      if (action.data_set === DATA_SETS.VESTA) {
        return {
          ...state,
          ...action.variables,
          calculated_fuel_load: calculateTFL(null, action.variables)
        };
      }
      break;
    case ActionTypes.CALCULATE_VESTA:
      const modifications = action.modifications ? action.modifications.properties.data : {};
      const variables = extractVariables(state);
      const new_table_data =
        calculateTableData(VALUES_CONFIG,
          action.include_history,
          action.short_future,
          action.weather_data.table_data,
          variables,
          modifications);
      const new_graph_data = convertToGraphData(GRAPHS_CONFIG, new_table_data);
      const ticks = extractTicks(new_graph_data, GRAPHS_CONFIG[0].id);

      return {
        ...state,
        ticks,
        graph_data: new_graph_data,
        table_data: new_table_data,
        calculated_fuel_load: calculateTFL(null, variables)
      };

    default:
      return state;
  }
  return state;
};

export const runParityCalcs = (wd, variables) => runParityCalcsFromConfig(VALUES_CONFIG, wd, variables);

export default reducer;



// WEBPACK FOOTER //
// ./src/reducers/point_data_vesta.js